import React, { useEffect, useMemo, useCallback } from 'react'
import { Dispatch } from 'redux'
import { Form, Field, FormikProps } from 'formik'
import { EditionType } from '@graphics/gfx-common/dist/constants'
import { IOption } from '../../types/common'
import { ICapabilityFormValues } from '../../types/capability'
import { getMediaTypeGroups } from '../../actions/mediaTypesGroups'
import { getPublishingLocations } from '../../actions/publishingLocations'
import {
  requiredMediaTypeGroupValidate,
  otherMediaTypeGroupValidate,
} from './validation'
import { StyledFormActionWrapper } from '../../layouts'
import {
  Button,
  H2,
  CustomTextField,
  DateAuthorFragment,
  CustomSelectField,
  CustomCheckboxGroupField,
  CustomFieldGroup,
} from '../../components'
import { StyledFormWrapper } from './styled'

export interface ICapabilityForm {
  publishingLocations: {
    publishingLocationsList: IOption[]
  }
  mediaTypesGroups: { list: IOption[] }
  dispatch: Dispatch
  cancelAction: () => void
  submitText: string
  editable?: boolean
  formikProps: FormikProps<ICapabilityFormValues>
}

export const CapabilityForm = ({
  publishingLocations: { publishingLocationsList },
  mediaTypesGroups,
  dispatch,
  cancelAction,
  submitText,
  editable = true,
  formikProps: { values, errors, touched, setFieldValue, isValid },
}: ICapabilityForm) => {
  useEffect(() => {
    dispatch(getMediaTypeGroups())
    dispatch(getPublishingLocations())
  }, [dispatch])

  const editionTypeChangeHandler = useCallback(() => {
    setFieldValue('editionSubType', '')
    setFieldValue('requiredMediaTypeGroupId', '')
    setFieldValue('supportedMediaTypeGroupId', '')
    setFieldValue('excludeMediaTypeGroupId', '')
  }, [setFieldValue])

  const disabledFields = useMemo(
    () => ({
      editionTypesFileOrFolder: values.editionTypes.some((item) =>
        [EditionType.DataStreamGraphic, EditionType.DataStreamData].includes(
          item
        )
      ),
      editionTypeDataStream: values.editionTypes.some((item) =>
        [EditionType.GraphicFile, EditionType.GraphicFolder].includes(item)
      ),
      requiredMediaTypeGroupId: !values.editionTypes.some((item) =>
        [EditionType.GraphicFile, EditionType.GraphicFolder].includes(item)
      ),
      supportedMediaTypeGroupId: !values.editionTypes.some((item) =>
        [EditionType.GraphicFolder].includes(item)
      ),
      excludeMediaTypeGroupId: !values.editionTypes.some((item) =>
        [EditionType.GraphicFolder].includes(item)
      ),
    }),
    [values.editionTypes]
  )

  return (
    <StyledFormWrapper>
      <H2 mb={4}>Enter metadata and save</H2>
      <Form>
        <Field
          name="title"
          label="Title"
          component={CustomTextField}
          disabled={!editable}
        />
        <Field
          name="description"
          label="Description"
          component={CustomTextField}
          multiline
          disabled={!editable}
        />
        <CustomFieldGroup
          label="Edition Types"
          error={
            touched.editionTypes && ((errors.editionTypes as any) as string)
          }
        >
          <Field
            name="editionTypes"
            component={CustomCheckboxGroupField}
            label="Graphic File"
            fieldValue="GraphicFile"
            disabled={disabledFields.editionTypesFileOrFolder || !editable}
            onChange={editionTypeChangeHandler}
          />
          <Field
            name="editionTypes"
            component={CustomCheckboxGroupField}
            label="Graphic Folder"
            fieldValue="GraphicFolder"
            disabled={disabledFields.editionTypesFileOrFolder || !editable}
            onChange={editionTypeChangeHandler}
          />
          <Field
            name="editionTypes"
            component={CustomCheckboxGroupField}
            label="DataStream Graphic"
            fieldValue="DataStreamGraphic"
            disabled={disabledFields.editionTypeDataStream || !editable}
            onChange={editionTypeChangeHandler}
          />
          <Field
            name="editionTypes"
            component={CustomCheckboxGroupField}
            label="DataStream Data"
            fieldValue="DataStreamData"
            disabled={disabledFields.editionTypeDataStream || !editable}
            onChange={editionTypeChangeHandler}
          />
        </CustomFieldGroup>
        <div className="short-fields">
          <Field
            name="editionSubType"
            label="Edition SubType"
            component={CustomSelectField}
            placeholder="Select Edition SubType"
            disabled={!editable}
          >
            {[
              { value: 'Standalone', label: 'Standalone' },
              { value: 'Interactive', label: 'Interactive' },
            ]}
          </Field>
          <Field
            name="requiredMediaTypeGroupId"
            label="Required media type group"
            placeholder="Select required media type group"
            component={CustomSelectField}
            disabled={disabledFields.requiredMediaTypeGroupId || !editable}
            validate={(value) =>
              requiredMediaTypeGroupValidate(value, values.editionTypes)
            }
          >
            {[...mediaTypesGroups.list]}
          </Field>
          <Field
            name="supportedMediaTypeGroupId"
            label="Supporting media type group"
            component={CustomSelectField}
            placeholder="Select supporting media type group"
            disabled={disabledFields.supportedMediaTypeGroupId || !editable}
            validate={(value) =>
              otherMediaTypeGroupValidate(value, values.editionTypes)
            }
          >
            {[...mediaTypesGroups.list]}
          </Field>
          <Field
            name="excludeMediaTypeGroupId"
            label="Exclude media type group"
            component={CustomSelectField}
            placeholder="Select exclude media type group"
            disabled={disabledFields.excludeMediaTypeGroupId || !editable}
            validate={(value) =>
              otherMediaTypeGroupValidate(value, values.editionTypes)
            }
          >
            {[...mediaTypesGroups.list]}
          </Field>
          <Field
            name="publishingLocation"
            label="Publishing Location"
            component={CustomSelectField}
            placeholder="Select publishing location"
            disabled={!editable}
          >
            {[...publishingLocationsList]}
          </Field>
          <CustomFieldGroup label="Promotion Destination">
            <Field
              name="promotionLevel"
              component={CustomCheckboxGroupField}
              label="Lynx Events"
              fieldValue="LynxEvents"
              disabled={!editable}
            />
            <Field
              name="promotionLevel"
              component={CustomCheckboxGroupField}
              label="Portal"
              fieldValue="Portal"
              disabled={!editable}
            />
          </CustomFieldGroup>
          <Field
            name="lifetime"
            label="Life time"
            type="number"
            component={CustomTextField}
            disabled={!editable}
          />
        </div>
        <DateAuthorFragment {...values} />
        <StyledFormActionWrapper>
          {!!cancelAction && (
            <Button id="capability-cancel-button" onClick={cancelAction}>
              Cancel
            </Button>
          )}
          <Button
            id="submit-form-data"
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid || !editable}
          >
            {submitText}
          </Button>
        </StyledFormActionWrapper>
      </Form>
    </StyledFormWrapper>
  )
}
